
import { useState, useRef } from 'react';
import { toast } from "sonner";
import { uploadFile, createEmbedContent } from '../utils/fileUpload';
import { supabase } from "@/integrations/supabase/client";

export const useContentEditor = (initialValue: string, onChange: (value: string) => void) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  // Handler for text changes in the textarea
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };
  
  // Handler for AI text formatting using OpenAI
  const handleAITextFormat = async () => {
    const content = initialValue.trim();
    
    if (!content) {
      toast.error('Voer eerst wat tekst in om op te maken');
      return;
    }
    
    setIsFormatting(true);
    toast.info('Bezig met AI-opmaak...', { id: 'ai-format' });
    
    try {
      const { data, error } = await supabase.functions.invoke('format-text-ai', {
        body: { text: content }
      });
      
      if (error) {
        throw error;
      }
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (data.formattedHtml) {
        onChange(data.formattedHtml);
        toast.success('Tekst is succesvol opgemaakt door AI', { id: 'ai-format' });
      } else {
        throw new Error('Geen opgemaakte HTML ontvangen van de AI');
      }
    } catch (error) {
      console.error('Error formatting text with AI:', error);
      toast.error(`Fout bij AI-opmaak: ${error.message || 'Onbekende fout'}`, { id: 'ai-format' });
    } finally {
      setIsFormatting(false);
    }
  };
  
  // Basic text formatting handler (existing functionality)
  const handleTextFormat = () => {
    // Format text with proper paragraph spacing and readability
    const formattedText = initialValue
      .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2
      .replace(/([.!?])\s*(?=\S)/g, '$1 ') // Ensure space after punctuation
      .split('\n')
      .map(paragraph => paragraph.trim())
      .filter(p => p.length > 0) 
      .join('\n\n');
      
    onChange(formattedText);
    toast.success('Tekst is opgemaakt voor betere leesbaarheid');
  };

  // Handler for inserting uploaded files (images or PDFs)
  const handleFileUpload = async (file: File, type: 'image' | 'pdf') => {
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      const publicUrl = await uploadFile(file, type);
      
      if (!publicUrl) {
        throw new Error('Geen URL gekregen voor het geüploade bestand');
      }
      
      // Insert content at cursor position or append to end
      const embedContent = createEmbedContent(publicUrl, type, file.name);
      const textArea = document.querySelector('textarea') as HTMLTextAreaElement;
      
      if (textArea) {
        const cursorPos = textArea.selectionStart;
        const currentContent = textArea.value;
        
        // Insert content at cursor position
        const newContent = 
          currentContent.substring(0, cursorPos) + 
          embedContent + 
          currentContent.substring(cursorPos);
          
        onChange(newContent);
        
        // Reset cursor position after the inserted content
        setTimeout(() => {
          textArea.selectionStart = cursorPos + embedContent.length;
          textArea.selectionEnd = cursorPos + embedContent.length;
          textArea.focus();
        }, 0);
      } else {
        // Just append if we can't find the textarea
        onChange(initialValue + embedContent);
      }
      
      toast.success(`${type === 'image' ? 'Afbeelding' : 'PDF'} succesvol toegevoegd`);
      
    } catch (error) {
      console.error(`Error in handleFileUpload:`, error);
    } finally {
      setIsUploading(false);
    }
  };
  
  // File input change handlers
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'image');
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  
  const handlePdfFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'pdf');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // Trigger file input clicks
  const handleImageUploadClick = () => {
    if (imageInputRef.current) imageInputRef.current.click();
  };
  
  const handlePdfUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  
  return {
    isUploading,
    isFormatting,
    fileInputRef,
    imageInputRef,
    handleTextChange,
    handleTextFormat,
    handleAITextFormat, // New method for AI formatting
    handleImageFileChange,
    handlePdfFileChange,
    handleImageUploadClick,
    handlePdfUploadClick
  };
};
